import { defineStore } from 'pinia';
import { computed } from 'vue';

import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';

import type { Distance } from '../models';
import type { HotelAmenityData } from '../../../core/models';
import { getHotelAmenitiesConfig } from '../../../core/models';

import { useHotelDetailsPanelStore } from './hotelDetailsPanelStore';
import {
  getChunkAmenities,
  getDistances,
  getMainDescription,
  getOfferedAmenitiesWithConfig,
  getOpeningParagraph,
  getShortAmenitiesGroups,
  getTagsUrls,
  shouldShowMoreAmenities,
} from './hotelDetailsDescriptionStoreLogic';

export const useHotelDetailsDescriptionStore = defineStore(
  'HotelDestailsDescriptionStore',
  () => {
    const browserStore = useBrowserStore();
    const hotelDetailsPanelStore = useHotelDetailsPanelStore();
    const statsigEventsStore = useStatsigEventsStore();

    // Getters
    const tagsUrls = computed<string[] | undefined>(() =>
      getTagsUrls(hotelDetailsPanelStore.hotelStaticData)
    );

    const openingParagraph = computed<string | undefined>(() =>
      getOpeningParagraph(hotelDetailsPanelStore.hotelStaticData)
    );

    const allOfferedAmenitiesWithConfig = computed<
      HotelAmenityData[] | undefined
    >(() =>
      getOfferedAmenitiesWithConfig(
        hotelDetailsPanelStore.hotelStaticData,
        getHotelAmenitiesConfig(),
        statsigEventsStore.logMissingAmenityConfiguration
      )
    );

    const fullAmenitiesGroups = computed<HotelAmenityData[][] | undefined>(
      () => {
        const chunkSize = 2;

        return getChunkAmenities(
          allOfferedAmenitiesWithConfig.value,
          browserStore.isSmallerThan1200px,
          chunkSize
        );
      }
    );

    const shortAmenitiesGroups = computed<HotelAmenityData[][] | undefined>(
      () => {
        const chunkSize = 2;

        return getShortAmenitiesGroups(
          allOfferedAmenitiesWithConfig.value,
          browserStore.isSmallerThan1200px,
          chunkSize
        );
      }
    );

    const shouldShowMoreAmenitiesButton = computed<boolean>(() =>
      shouldShowMoreAmenities(
        allOfferedAmenitiesWithConfig.value,
        shortAmenitiesGroups.value
      )
    );

    const shortMainDescription = computed<string | undefined>(() =>
      getMainDescription(
        hotelDetailsPanelStore.hotelStaticData,
        false,
        tagsUrls.value,
        openingParagraph.value,
        { tagsEquivalent: 40, toFillSpaceNextToMap: 300 }
      )
    );

    const fullMainDescription = computed<string | undefined>(() =>
      getMainDescription(
        hotelDetailsPanelStore.hotelStaticData,
        true,
        tagsUrls.value,
        openingParagraph.value,
        { tagsEquivalent: 40, toFillSpaceNextToMap: 300 }
      )
    );

    const distances = computed<Distance[] | undefined>(() =>
      getDistances(hotelDetailsPanelStore.hotelStaticData)
    );

    return {
      // Getters
      tagsUrls,
      openingParagraph,
      shortAmenitiesGroups,
      fullAmenitiesGroups,
      shouldShowMoreAmenitiesButton,
      shortMainDescription,
      fullMainDescription,
      distances,
    };
  }
);
